import { useEffect, useState } from "react";
import { Loader2, Clock, GitBranch, UserCheck, Edit3, Archive, Trash2, PlusCircle } from "lucide-react";
import { api } from "../../lib/api";

const ICONS = {
    stage_transition: GitBranch,
    tutor_assigned: UserCheck,
    edit: Edit3,
    archive: Archive,
    delete: Trash2,
    lead_created: PlusCircle,
};

/**
 * LeadTimeline — chronological audit log for a single lead pulled from
 * /api/admin/pipeline/registrations/{id}/timeline. Auto-refreshes when the
 * parent re-mounts (LeadDetailModal does this after every action).
 */
export default function LeadTimeline({ leadId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/pipeline/registrations/${leadId}/timeline`)
            .then((r) => { if (!cancelled) setEvents(r.data.events || []); })
            .catch(() => { /* silent — empty timeline acceptable */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [leadId]);

    return (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="lead-timeline">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <h4 className="font-heading font-semibold text-[#0F172A]">Pipeline Timeline</h4>
                <span className="text-xs text-slate-400" data-testid="lead-timeline-count">({events.length} events)</span>
            </div>
            <div className="px-5 py-3">
                {loading && (<div className="text-sm text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>)}
                {!loading && !events.length && (
                    <div className="text-sm text-slate-400 py-4 text-center">No events yet — actions you take here will be logged automatically.</div>
                )}
                {!loading && events.map((e, idx) => {
                    const Icon = ICONS[e.kind] || Clock;
                    return (
                        <div key={e.id} className="flex gap-3 py-2 border-b border-slate-50 last:border-b-0" data-testid={`timeline-event-${idx}`}>
                            <div className="shrink-0 w-7 h-7 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                                <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#0F172A]">{e.summary}</div>
                                <div className="text-xs text-slate-400">
                                    {new Date(e.at).toLocaleString()} · {e.actor}
                                </div>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0 self-center">{e.kind.replace(/_/g, " ")}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
